
import './SearchRequest.css';
import {
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  SelectChangeEvent
} from '@mui/material';
import React, { useState, useEffect, ReactNode, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { t } from 'i18next';
import { fetchRequestFromSearch, reset as resetSearch } from './requestSearchSlice';
import { URLSearchParamsInit, useLocation, useSearchParams } from 'react-router-dom';
import SelectDocumentType from '../Student/SelectDocumentType';
import googleRecaptchaActions from '../../shared/google-recaptcha-v3/googleRecaptchaActions';
import { useRecaptcha } from '../../shared/google-recaptcha-v3/useRecaptcha';
import { reset } from '../Request/requestSlice';
import useRuleValidation from '../../shared/hooks/useRuleValidation';
import RequiredFieldMessage from '../../components/RequiredFieldMessage';
import { useCurrentUserProfile } from '../User/userSlice';
import { useAppContext } from '../App.Context';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../shared/redux/store';
import { EquivalencyRequestSearchParams } from '../../models/EquivalencyRequest';
import DocumentTypes from '../../models/DocumentTypes';
import RequestDetail from './RequestDetail/RequestDetail';
import Activity from '../../models/Activity';

const SearchRequest: React.FC = () => {
  const location = useLocation();
  const [searchUrlParams, setSearchParams] = useSearchParams(location.search);
  const request = useAppSelector((state) => state.requestSearch.searchRequest);
  const student = useCurrentUserProfile();
  const isLoadingProfile = useAppSelector((state) => state.user.isLoadingProfile);
  const { user } = useAppContext();
  const defaultSearch = !!location.search && {
    documentType: searchUrlParams.get('documentType') ? (searchUrlParams.get('documentType') ?? '') as DocumentTypes : DocumentTypes.dni,
    documentNumber: searchUrlParams.get('documentNumber') ?? '',
    caseNumber: searchUrlParams.get('caseNumber') ?? ''
  };
  const { callIfHuman, reCaptchaToken, fetchOnce } = useRecaptcha(googleRecaptchaActions.requestSearch);
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentType, setDocumentType] = useState(DocumentTypes.dni);
  const [caseNumber, setCaseNumber] = useState('');
  const [searched, setSearched] = useState(false);
  const caseNumberRule = useRuleValidation(() => !!caseNumber);
  const documentTypeRule = useRuleValidation(() => !!documentType);
  const documentNumberRule = useRuleValidation(() => !!documentNumber);
  const [activitiesFile, setActivitiesFile] = useState([]);

  const searchParams: EquivalencyRequestSearchParams = {
    documentType,
    documentNumber,
    caseNumber,
    reCaptchaToken,
    offset: 0,
    pageSize: 1
  };

  const handleReset = () => {
    setDocumentNumber('');
    setDocumentType(DocumentTypes.dni);
    setCaseNumber('');
    dispatch(reset());
    dispatch(resetSearch());
    setSearched(false);
  };

  const canSearch = ![
    caseNumberRule.valid,
    documentTypeRule.valid,
    documentNumberRule.valid
  ].includes(false);

  const isLoadingUserData = useMemo(() => {
    const result = user?.valid && isLoadingProfile;
    return result
  }, [user, isLoadingProfile]);

  const studenDocuments = useMemo(() => {
    return request?.equivalencies?.files || [];
  }, [request]);

  const dispatch = useAppDispatch();

  const search = (searchParams: EquivalencyRequestSearchParams) => callIfHuman(() => {
    setSearched(true);
    dispatch(fetchRequestFromSearch(searchParams));
  });

  const handleDocumentType = (event: SelectChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setDocumentType(value as DocumentTypes.dni);
    setSearchParams({
      ...defaultSearch,
      documentType: value
    } as URLSearchParamsInit);
  };

  const handleDocumentNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDocumentNumber(value);
    setSearchParams({
      ...defaultSearch,
      documentNumber: value
    } as URLSearchParamsInit);
  };

  const handleCaseNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCaseNumber(value);
    setSearchParams({
      ...defaultSearch,
      caseNumber: value
    } as URLSearchParamsInit);
  };
  const researchRequest = () => {
    search(searchParams);
  }

  useEffect(() => {
    if (defaultSearch &&
      defaultSearch.documentType.length > 0 &&
      defaultSearch.caseNumber.length > 0 &&
      defaultSearch.documentNumber.length > 0 &&
      fetchOnce) {
      search({
        ...defaultSearch,
        reCaptchaToken,
        offset: 0,
        pageSize: 1
      });
    }
  }, [reCaptchaToken, fetchOnce]);

  useEffect(() => {
    if (student) {
      setDocumentType(student?.documentType?.name);
      setDocumentNumber(student?.documentNumber);
    }
  }, [student]);

  // resetComponent data
  useEffect(() => {
    if (!student && defaultSearch) {
      setDocumentNumber(defaultSearch.documentNumber);
      setDocumentType(defaultSearch.documentType);
      setCaseNumber(defaultSearch.caseNumber);
    }
    return () => {
      dispatch(reset());
      dispatch(resetSearch());
    };
  }, []);

  const refreshActivitiesDocument = () => {
    setActivitiesFile([]);
    if (request?.equivalencies?.activities !== undefined && request.equivalencies.activities.length > 0) {
      setActivitiesFile(request.equivalencies.activities.filter((activity: Activity) => activity.program_file !== null).map((filterActivity: Activity) => {
        return {
          ...filterActivity.program_file,
          file_type: filterActivity.activity?.name
        }
      }));
    }
  }

  useEffect(() => {
    refreshActivitiesDocument()
    if (request) {
      setCaseNumber(request.equivalencies.case_number)
      setSearchParams({
        ...defaultSearch,
        caseNumber: request.equivalencies.case_number
      } as URLSearchParamsInit);
    }
  }, [request]);

  return (
    <div className="REQXXI-Search-Container">
      <Typography
        className="REQXXI-Search-Title"
        color="primary"
        variant="h4"
        fontWeight="500"
      >
        <>{t('searchEquivalencies')}</>
      </Typography>
      <Box className="REQXXI-Search-Fields">
        <TextField
          fullWidth
          tabIndex={-1}
          id='caseNumber-input'
          data-testid={'caseNumber-input'}
          label={<>{t('trackingNumber')}</>}
          onChange={handleCaseNumber}
          value={caseNumber || ''}
          InputProps={{
            endAdornment: caseNumberRule.adornment,
            inputProps: { tabIndex: 6 }
          }}
        />
        <Box sx={{ width: '100%' }} display="flex" gap="1rem">
          <SelectDocumentType
            size='medium'
            onChange={handleDocumentType}
            value={documentType}
            name="document_type"
            multiColumn
            rule={documentTypeRule}
            showLoading={isLoadingUserData}
          />
          <TextField
            fullWidth
            id='documentNumber-input'
            data-testid={'documentNumber-input'}
            value={documentNumber || ''}
            onChange={handleDocumentNumber}
            label={<>{t('documentNumber')}</>}
            InputProps={{
              endAdornment: isLoadingUserData ? <CircularProgress color="inherit" size={20} /> : documentNumberRule.adornment
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <RequiredFieldMessage />
        </Box>
      </Box>
      <Grid
        direction='row'
        justifyContent="end"
        alignItems="center"
        container
        className='REQXXI-Button-Option-Container'
      >
        <Grid
          item
          xs={12}
          md={4}
          lg={3}
        >
          <Button onClick={() => handleReset()} className='REQXXI-Trash-Button'>
            <DeleteIcon />
            <Typography sx={{ paddingLeft: '10px' }}>{t('cleanFields') as string}</Typography>
          </Button>
        </Grid>

        <Grid
          item
          alignContent="center"
          xs={12} md={3} lg={2}
          className='REQXXI-Search-Button-Container'
        >
          <Button
            id='searchEquivalencyRequest'
            disabled={!canSearch}
            className="REQXXI-Search-Button"
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => search(searchParams)}
          >
            <>{t('searchLabel')}</>
          </Button>
        </Grid>
      </Grid>
      <br />
      {request &&
        <Box marginTop={2} marginBottom={30}>
          <Typography
            variant="h6"
            color="primary"
            mb={2}
            mt={6}
          >
            {t('manageRequestEquivalencies') as string}
          </Typography>
          <RequestDetail canSearch={canSearch} researchRequest={researchRequest} request={request.equivalencies} studenDocuments={studenDocuments} activityPrograms={activitiesFile} />
        </Box>
      }
      {
        searched && !request &&
        <Box marginTop={2} className='REQXXI-SearchRequest-AlertMessage-Warning'>
          <Alert severity='warning' >
            {t('noEquivalencyRequestsWhereFound') as unknown as ReactNode}
          </Alert>
        </Box>
      }
    </div >
  );
};

export default React.memo(SearchRequest);
